import { useEffect, useState } from 'react';

import { Notification01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  Button,
  Input,
  InputGroup,
  PhoneNumberPicker,
  Select,
  ToggleSwitchInput,
} from 'components/FormComponents';

export const CandidateInformation = ({
  candidate,
  company,
  brand,
  teams,
  companyUsers,
  onUpdate = () => {},
}) => {
  const { register, reset, control, handleSubmit } = useForm({ defaultValues: candidate });

  const team_id = useWatch({ control, name: 'team_id' });
  const user_id = useWatch({ control, name: 'user_id' });

  const [ccNotificationEmailsEnabled, setCcNotificationEmailsEnabled] = useState(
    !!candidate.cc_notification_emails,
  );

  useEffect(() => reset(candidate), [candidate]);

  useEffect(() => {
    setCcNotificationEmailsEnabled(!!candidate.cc_notification_emails);
  }, [candidate?.cc_notification_emails]);

  const handleUpdate = async data => {
    return onUpdate({
      ...data,
      cc_notification_emails: ccNotificationEmailsEnabled ? data?.cc_notification_emails : '',
    });
  };

  return (
    <form
      className="d-flex flex-column u-width-100 u-gap-medium background-secondary u-padding"
      onSubmit={handleSubmit(handleUpdate)}>
      <div className={cn('card', 'card-with-border', 'u-padding')}>
        <div className="inline">
          <InputGroup title="First name">
            <Input
              name="first_name"
              register={register}
              validators={{ required: true }}
              placeholder="John"
            />
          </InputGroup>
          <InputGroup title="Last name">
            <Input
              name="last_name"
              register={register}
              validators={{ required: true }}
              placeholder="Doe"
            />
          </InputGroup>
        </div>
        <InputGroup title="Email">
          <Input
            name="email"
            register={register}
            validators={{ required: true }}
            placeholder="john@checkmate.com"
          />
        </InputGroup>
        <InputGroup title="Contact Number">
          <Controller
            control={control}
            name="contact_number"
            render={({ field: { onChange, value } }) => (
              <PhoneNumberPicker
                value={value}
                onChange={onChange}
                defaultCountry={company?.country}
              />
            )}
          />
        </InputGroup>
        <InputGroup title="Job title">
          <Input name="job_role" register={register} placeholder="General Manager" />
        </InputGroup>
        <InputGroup title="Department">
          <Input name="department" register={register} placeholder="Department" />
        </InputGroup>

        <InputGroup title="Reference Code">
          <Input name="reference_code" register={register} placeholder="REFCODE1234" />
        </InputGroup>

        <InputGroup title="Brand">
          <Input disabled={true} inputProps={{ value: brand?.name }} />
        </InputGroup>

        {teams.length > 1 && (
          <InputGroup title="Team" className="u-margin-bottom--large">
            <Select
              name="team_id"
              placeholder="Team"
              register={register}
              value={team_id}
              useDefault>
              {teams.map(team => (
                <Select.Item key={team.id} value={team.id}>
                  {team.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        )}

        <InputGroup title="Document">
          <Input disabled={true} register={register} name="document_name" placeholder="-" />
        </InputGroup>
      </div>

      <div className={cn('card', 'card-with-border', 'u-padding')}>
        <div>
          <div className={cn('u-bold')}>Candidate Assignment</div>
          <InputGroup
            title="The user who is assigned to manage the candidate and receive notifications. This can be updated at any time."
            className="u-margin-bottom--0">
            <Select
              name="user_id"
              placeholder="User"
              register={register}
              value={user_id}
              useDefault
              useSearch>
              {companyUsers?.map(user => (
                <Select.Item key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} • {user.email}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        </div>
      </div>

      <ToggleSwitchInput
        noMaxWidth
        text="Notification List"
        subtext="Add external emails to get notified about status updates"
        onChange={e => setCcNotificationEmailsEnabled(e.target.checked)}
        icon={Notification01Icon}
        className="u-margin-bottom--0"
        value={ccNotificationEmailsEnabled}>
        <InputGroup title="Emails">
          <div className="d-flex flex-column u-width-100 u-gap-small">
            <Input
              name="cc_notification_emails"
              placeholder="email_1@example.com,email_2@example.com"
              register={register}
              disabled={!ccNotificationEmailsEnabled}
            />
            <span className="u-font-size-x-small secondary-text">
              Separate multiple emails with commas (e.g., jane@example.com, john@example.com)
            </span>
          </div>
        </InputGroup>
      </ToggleSwitchInput>

      <Button submit className="u-width-100">
        Update
      </Button>
    </form>
  );
};
